import * as stylex from '@stylexjs/stylex';

/**
 * KEYFRAMES
 */
const twinkle = stylex.keyframes({
  '0%, 100%': { opacity: 0.3 },
  '50%': { opacity: 0.8 },
});

const rotate = stylex.keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' },
});



const pulse = stylex.keyframes({
  '0%, 100%': { transform: 'rotate(45deg) scale(1)', opacity: 0.03 },
  '50%': { transform: 'rotate(45deg) scale(1.1)', opacity: 0.06 },
});

const shimmerLayout = stylex.keyframes({
  '0%': { transform: 'translateX(-100%)' },
  '100%': { transform: 'translateX(100%)' },
});

/**
 * STYLES
 */
export const layoutStyles = stylex.create({
  /* Header Styles */
  header: {
    backgroundColor: 'transparent',
    paddingBlock: '1rem',
    paddingInline: 0,
    marginBottom: '0.5rem',
    boxShadow: 'var(--shadow-sm)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backdropFilter: 'blur(8px)',
    transitionProperty: 'background-color, box-shadow',
    transitionDuration: 'var(--transition-normal)',
  },
  headerContent: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '64px',
  },
  navLink: {
    color: {
      default: 'var(--text-secondary)',
      ':hover': 'var(--primary)',
    },
    textDecoration: 'none',
    padding: '0.5rem',
    borderRadius: 'var(--radius-sm)',
    position: 'relative',
    zIndex: 2,
    backgroundColor: {
      default: 'transparent',
      ':hover': 'var(--bg-hover)',
    },
    transitionProperty: 'all',
    transitionDuration: 'var(--transition-fast)',
  },
  navLinkActive: {
    color: 'var(--primary)',
    fontWeight: 500,
  },
  /* Logo */
  logo: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'bold',
    color: 'var(--text-primary)',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transitionProperty: 'color',
    transitionDuration: 'var(--transition-fast)',
    ':hover': {
      color: 'hsl(var(--hue-primary), 85%, 70%);',
      // Custom property to control child opacity
      // Note: This requires the variable to be defined in global CSS or allowed by StyleX configuration
      // Assuming straightforward CSS variable usage works in this setup or falling back to a different strategy if not.
      // StyleX generally advises against arbitrary CSS variables for logic, but for simple parent-hover it's often the cleanest escape hatch.
      '--logo-version-opacity': 1,
    } as any,
  },
  logoAccent: {
    backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  },
  logoVersion: {
    fontSize: 'calc(var(--font-size-xs) * 0.9)',
    color: 'var(--text-tertiary)',
    fontWeight: 'normal',
    marginLeft: 0,
    position: 'relative',
    top: '-8px',
    opacity: {
      default: 'var(--logo-version-opacity, 0)',
      '@media (max-width: 768px)': 1,
    },
    transitionProperty: 'opacity',
    transitionDuration: 'var(--transition-slow)',
  },

  /* Desktop Actions */
  desktopActions: {
    display: {
      default: 'flex',
      '@media (max-width: 768px)': 'none',
    },
    alignItems: 'center',
    gap: '0.5rem',
  },

  /* Mobile Actions */
  mobileActions: {
    display: {
      default: 'none',
      '@media (max-width: 768px)': 'flex',
    },
    alignItems: 'center',
    gap: '0.5rem',
  },

  /* Sidebar Toggle Button */
  sidebarToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bg-surface)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--border)',
    borderRadius: 'var(--radius-sm)',
    padding: '0.5rem',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    transitionProperty: 'all',
    transitionDuration: 'var(--transition-fast)',
    marginRight: '0.5rem',
  },

  sidebarToggleHover: {
    backgroundColor: 'var(--bg-hover)',
    color: 'var(--text-primary)',
    borderColor: 'var(--border-hover)',
  },

  /* Sidebar Close Button */
  sidebarClose: {
    display: 'none',
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    backgroundColor: 'var(--bg-surface)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--border)',
    borderRadius: 'var(--radius-full)',
    width: '32px',
    height: '32px',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    transitionProperty: 'all',
    transitionDuration: 'var(--transition-fast)',
    zIndex: 1002,
  },

  sidebarCloseHover: {
    backgroundColor: 'var(--bg-hover)',
    color: 'var(--text-primary)',
    borderColor: 'var(--border-hover)',
    transform: 'scale(1.1)',
  },

  /* Theme Toggle */
  themeToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 'var(--radius-full)',
    cursor: 'pointer',
    color: {
      default: 'var(--text-secondary)',
      ':hover': 'var(--primary)',
    },
    transitionProperty: 'all',
    transitionDuration: 'var(--transition-normal)',
    position: 'relative',
    zIndex: 2,
  },

  themeToggleHover: {
    color: 'var(--primary)',
    transform: 'rotate(15deg) scale(1.1)',
    backgroundColor: 'var(--bg-hover)',
    filter: 'drop-shadow(0 0 8px var(--primary))',
  },

  themeToggleActive: {
    transform: 'translateY(0)',
    color: 'var(--text-primary)',
  },

  themeToggleDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  themeIcon: {
    fontSize: '1.2rem',
  },

  /* Mobile Theme Toggle */
  mobileThemeToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 'var(--radius-full)',
    color: {
      default: 'var(--text-secondary)',
      ':hover': 'var(--primary)',
    },
    cursor: 'pointer',
    transitionProperty: 'all',
    transitionDuration: 'var(--transition-fast)',
  },

  mobileThemeToggleHover: {
    color: 'var(--primary)',
    transform: 'rotate(15deg) scale(1.1)',
    backgroundColor: 'var(--bg-hover)',
    filter: 'drop-shadow(0 0 8px var(--primary))',
  },

  /* Animated Background */
  animatedBackground: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: -1,
    pointerEvents: 'none',
    overflow: 'hidden',
  },

  /* Stars Container */
  starsContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    transitionProperty: 'transform',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease-out',
  },

  star: {
    position: 'absolute',
    width: '2px',
    height: '2px',
    backgroundColor: 'var(--border-hover)',
    borderRadius: '50%',
    animationName: twinkle,
    animationDuration: '5s',
    animationIterationCount: 'infinite',
    opacity: 0.6,
  },

  /* Geometric Shapes */
  geometricShapes: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    zIndex: 1,
    pointerEvents: 'none',
  },

  shape: {
    position: 'absolute',
    opacity: 0.03,
  },

  triangle: {
    width: 0,
    height: 0,
    borderLeftWidth: '80px',
    borderLeftStyle: 'transparent',
    borderRightWidth: '80px',
    borderRightStyle: 'transparent',
    borderBottomWidth: '140px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'var(--secondary)',
    top: '15%',
    right: '10%',
    animationName: rotate,
    animationDuration: '20s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    transitionProperty: 'border-bottom-color',
    transitionDuration: 'var(--transition-normal)',
  },

  square: {
    width: '100px',
    height: '100px',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: 'var(--primary)',
    top: '70%',
    right: '20%',
    transform: 'rotate(45deg)',
    animationName: pulse,
    animationDuration: '10s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    transitionProperty: 'border-color',
    transitionDuration: 'var(--transition-normal)',
  },

  /* Documentation Layout */
  docsLayout: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 1,
  },

  /* Documentation Header */
  docsHeader: {
    backgroundColor: 'var(--bg-card)',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'var(--border)',
    padding: '1rem 0',
    position: 'sticky',
    top: '64px',
    zIndex: 100,
    backdropFilter: 'blur(8px)',
  },

  docsHeaderContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: {
      default: '0 var(--spacing-md)',
      '@media (max-width: 480px)': '0 var(--spacing-sm)',
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  docsHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
  },

  docsHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
  },

  docsLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    fontSize: 'var(--font-size-sm)',
    transitionProperty: 'color',
    transitionDuration: 'var(--transition-fast)',
  },

  docsLogoHover: {
    color: 'var(--text-primary)',
  },

  logoIcon: {
    width: '16px',
    height: '16px',
  },

  logoText: {
    fontWeight: 500,
  },

  /* Main Content Area */
  docsMain: {
    flex: 1,
    display: 'flex',
    minHeight: 'calc(100vh - 64px)',
    transitionProperty: 'all',
    transitionDuration: 'var(--transition-normal)',
    position: 'relative',
    flexDirection: {
      default: 'row',
      '@media (max-width: 768px)': 'column',
    },
    gap: {
      default: '2rem',
      '@media (max-width: 768px)': '0',
    },
  },

  docsContent: {
    flex: 1,
    padding: {
      default: 'var(--spacing-xl)',
      '@media (max-width: 768px)': 'var(--spacing-lg)',
      '@media (max-width: 480px)': 'var(--spacing-md)',
    },
    maxWidth: '100%',
    transitionProperty: 'padding',
    transitionDuration: 'var(--transition-normal)',
  },

  /* Sidebar */
  sidebar: {
    position: {
      default: 'relative',
      '@media (max-width: 768px)': 'fixed',
    },
    width: 'var(--sidebar-width)',
    minHeight: 'calc(100vh - 64px)',
    backgroundColor: {
      default: 'transparent',
      '@media (max-width: 768px)': 'var(--bg)',
    },
    borderRightWidth: {
      default: '1px',
      '@media (max-width: 768px)': '0',
    },
    borderRightStyle: 'solid',
    borderRightColor: 'var(--border)',
    zIndex: {
      default: 1,
      '@media (max-width: 768px)': 999,
    },
    transform: {
      default: 'translateZ(0)',
      '@media (max-width: 768px)': 'translateX(-100%)',
    },
    willChange: 'transform',
    flexShrink: 0,
    boxShadow: {
      default: 'var(--shadow-sm)',
      '@media (max-width: 768px)': 'var(--shadow-xl)',
    },
    backdropFilter: {
      default: 'blur(4px)',
      '@media (max-width: 768px)': 'none',
    },
    transitionProperty: 'transform',
    transitionDuration: 'var(--transition-normal)',
    transitionTimingFunction: 'ease-in-out',
    top: {
      default: '0',
      '@media (max-width: 768px)': 'calc(64px + 2rem)',
    },
    height: {
      default: 'auto',
      '@media (max-width: 768px)': 'calc(100dvh - (64px + 2rem))',
    },
    overflowY: {
      default: 'visible',
      '@media (max-width: 768px)': 'auto',
    },
    left: 0,
  },
  sidebarOpen: {
    transform: {
      default: 'translateZ(0)',
      '@media (max-width: 768px)': 'translateX(0)',
    },
  },
  sidebarOverlay: {
    position: 'fixed',
    top: 'calc(64px + 2rem)',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
    opacity: 0,
    pointerEvents: 'none',
    transitionProperty: 'opacity',
    transitionDuration: 'var(--transition-normal)',
    backdropFilter: 'blur(2px)',
    display: {
      default: 'none',
      '@media (max-width: 768px)': 'block',
    }
  },
  sidebarOverlayOpen: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  sidebarContent: {
    padding: 'var(--spacing-xl)',
    overflowY: 'auto',
  },
  sidebarSection: {
    marginBottom: 'var(--spacing-xl)',
    ':last-child': {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: '10px',
    marginBottom: '10px',
    paddingBottom: '0px',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'var(--border)',
  },
  sidebarSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-sm)',
  },
  sectionIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: {
      default: 'var(--text-secondary)',
      // Targeting parent hover in StyleX is done by the parent 
      // passing a style or using a shared variable.
      // For direct translation, we use the specific section hover states:
    },
    transitionProperty: 'color',
    transitionDuration: 'var(--transition-fast)',
  },

  // Icon Hover Variants (To be applied based on section class)
  iconGettingStarted: { ':hover': { color: '#ffffff' } },
  iconCoreFeatures: { ':hover': { color: '#ffffff' } },
  iconImageModels: { ':hover': { color: '#ffd966' } },
  iconVideoModels: { ':hover': { color: '#ff79c6' } },
  iconSpeechModels: { ':hover': { color: '#50fa7b' } },
  iconMusicModels: { ':hover': { color: '#c2a7fc' } },
  iconSoundEffects: { ':hover': { color: '#8be9fd' } },
  iconChatModels: { ':hover': { color: '#4299F0' } },
  iconSupport: { ':hover': { color: '#ffffff' } },

  sidebarNav: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  sidebarNavItem: {
    marginBottom: 'var(--spacing-xs)',
  },
  sidebarNavLink: {
    display: 'block',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    color: {
      default: 'var(--text-secondary)',
      ':hover': 'var(--text-primary)',
    },
    textDecoration: 'none',
    borderRadius: 'var(--radius-md)',
    transitionProperty: 'all',
    transitionDuration: 'var(--transition-fast)',
    fontSize: 'var(--font-size-sm)',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: {
      default: 'transparent',
      ':hover': 'rgba(var(--bg-hover-rgb, 17, 17, 17), 0.6)',
    },
    transform: {
      default: 'translateX(0)',
      ':hover': 'translateX(4px)',
    },
  },
  sidebarNavLinkActive: {
    color: 'var(--primary)',
    backgroundColor: 'rgba(var(--primary-rgb, 60, 60, 60), 0.15)',
    fontWeight: 500,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'var(--primary)',
    paddingLeft: 'calc(var(--spacing-md) - 3px)',
  },
  // Shimmer element (::before)
  navLinkBefore: {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: 'linear-gradient(90deg, transparent, rgba(var(--primary-rgb, 60, 60, 60), 0.1), transparent)',
    transform: 'translateX(-100%)',
    animationName: shimmerLayout,
    animationDuration: '2s',
    animationIterationCount: 'infinite',
  },
  navChildren: {
    marginLeft: 'var(--spacing-md)',
    marginTop: 'var(--spacing-xs)',
  },
  navLevel1: {
    marginLeft: 'var(--spacing-md)',
  },
  /* Focus States */
  focusVisible: {
    outline: '2px solid var(--focus-ring)',
    outlineOffset: '2px',
  },


});